import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit3, Trash2, X, Save, Loader2, Search, Copy, ChevronLeft, ArrowUp, ArrowDown } from 'lucide-react';

/**
 * QuoteTypesManagePage · iter298s (Tier B)
 *
 * Simple full-CRUD admin for the `quote_types` collection. Everything
 * else in the app that surfaces a quote_type (Studio's Quotation Letter
 * editor, PropertyDetail's + New Appraisal picker, the Quotation Letter
 * PDF) reads from this collection — so this page is the source of truth
 * for adding / renaming / re-categorising / deleting types.
 *
 * Backend endpoints (all already exist):
 *   GET    /quote-types                — list
 *   GET    /quote-types/{ref}          — single (unused here; table loads all)
 *   PUT    /quote-types/{ref}          — upsert (create when ref is new)
 *   DELETE /quote-types/{ref}
 *   GET    /quote-types-enums          — dropdown options for roof/category
 */
export default function QuoteTypesManagePage() {
  const { api } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterRoof, setFilterRoof] = useState('');
  const [editing, setEditing] = useState(null);      // full row being edited
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enums, setEnums] = useState({ roof_types: [], quote_categories: [] });
  // iter299w — Sortable table. Default sort = ref asc. Clicking a
  // column header cycles asc → desc → default. `null` = default.
  const [sort, setSort] = useState(null); // { col: 'category'|'roof'|'ref', dir: 'asc'|'desc' } | null

  const cycleSort = (col) => {
    setSort(prev => {
      if (!prev || prev.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null; // third click resets to default (ref asc)
    });
  };
  const sortIcon = (col) => {
    if (!sort || sort.col !== col) return null;
    return sort.dir === 'asc' ? <ArrowUp size={10} className="inline text-emerald-700" /> : <ArrowDown size={10} className="inline text-emerald-700" />;
  };

  // ─── Load ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [{ data: list }, { data: en }] = await Promise.all([
        api.get('/quote-types'),
        api.get('/quote-types-enums'),
      ]);
      setRows((Array.isArray(list) ? list : []).slice().sort((a, b) => (a.ref || '').localeCompare(b.ref || '')));
      setEnums(en || { roof_types: [], quote_categories: [] });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // ─── Filters ─────────────────────────────────────────────────────────
  const filtered = rows.filter(r => {
    if (q && !(r.ref || '').toLowerCase().includes(q.toLowerCase())) return false;
    if (filterCat && r.quote_category !== filterCat && !(r.quote_categories || []).includes(filterCat)) return false;
    if (filterRoof && r.roof_type !== filterRoof && !(r.roof_types || []).includes(filterRoof)) return false;
    return true;
  });

  // iter299w — Apply the tri-state sort ON TOP of the alphabetical
  // base sort applied at load time (`load()` sorts by ref asc). The
  // secondary tiebreak is always the alphabetical ref so rows within
  // the same category/roof group stay predictable.
  const visible = useMemo(() => {
    if (!sort) return filtered; // default = ref asc (already applied by load())
    const getKey = (row) => {
      if (sort.col === 'ref') return (row.ref || '').toLowerCase();
      if (sort.col === 'category') {
        const arr = (row.quote_categories || []);
        return (arr.length > 0 ? arr.join(', ') : (row.quote_category || '')).toLowerCase();
      }
      if (sort.col === 'roof') {
        const arr = (row.roof_types || []);
        return (arr.length > 0 ? arr.join(', ') : (row.roof_type || '')).toLowerCase();
      }
      return '';
    };
    const sorted = filtered.slice().sort((a, b) => {
      const ka = getKey(a); const kb = getKey(b);
      // Empty values sort LAST regardless of direction so "—" rows don't
      // clutter the top.
      if (!ka && kb) return 1;
      if (ka && !kb) return -1;
      const cmp = ka.localeCompare(kb);
      if (cmp !== 0) return sort.dir === 'asc' ? cmp : -cmp;
      // Tiebreak on ref alphabetically (asc) — keeps sibling groups stable.
      return (a.ref || '').localeCompare(b.ref || '');
    });
    return sorted;
  }, [filtered, sort]);

  // ─── CRUD ────────────────────────────────────────────────────────────
  const startAdd = () => {
    setEditing({
      ref: '',
      description: '',
      details: '',
      completion_description: '',
      completion_details: '',
      quote_category: '',
      quote_categories: [],
      roof_type: '',
      roof_types: [],
      _isNew: true,
    });
  };
  const startEdit = (row) => setEditing({ ...row });
  const startDuplicate = (row) => {
    setEditing({ ...row, ref: `${row.ref} (copy)`, _isNew: true });
  };
  const doSave = async (payload) => {
    if (!payload.ref?.trim()) { setError('Quote type name required'); return; }
    setBusy(true);
    try {
      const { _isNew, _originalRef, ...body } = payload;
      void _isNew;
      const finalRef = payload.ref.trim();
      // iter299w — Rename detection. When editing (not new) and the ref
      // changed, call the dedicated rename endpoint first. Backend will
      // 409 if `finalRef` is taken; per user's explicit choice (Feb-21),
      // we do NOT cascade the new name to `appraisals.quote_type_ref` —
      // old appraisals will keep pointing at the old string and may
      // silently render defaults. The modal shows a warning before this
      // request is fired.
      if (!_isNew && _originalRef && _originalRef !== finalRef) {
        await api.post(`/quote-types/${encodeURIComponent(_originalRef)}/rename`, { new_ref: finalRef });
      }
      await api.put(`/quote-types/${encodeURIComponent(finalRef)}`, body);
      await load();
      setEditing(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/quote-types/${encodeURIComponent(confirmDelete.ref)}`);
      await load();
      setConfirmDelete(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4] p-6" data-testid="quote-types-manage-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => nav('/script-audit')} className="flex items-center gap-1 text-xs text-slate-600 hover:text-emerald-700" data-testid="qtm-back">
            <ChevronLeft size={14} /> Back to Studio
          </button>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-lg overflow-hidden">
          {/* Header */}
          <div className="px-5 py-3 bg-gradient-to-r from-emerald-50 to-white border-b border-emerald-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-lg font-bold text-emerald-900">Quote Types</h1>
              <p className="text-[11px] text-emerald-800/60">Source of truth for the Quotation Letter&apos;s <b>Includes</b> + <b>Notes</b> lists. Also drives the &ldquo;+ New Appraisal&rdquo; picker.</p>
            </div>
            <button onClick={startAdd} data-testid="qtm-add" className="flex items-center gap-1 px-3 py-1.5 bg-emerald-700 text-white text-xs font-semibold rounded hover:bg-emerald-800">
              <Plus size={12} /> New Quote Type
            </button>
          </div>
          {/* Filters */}
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap bg-slate-50">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
                data-testid="qtm-search"
                className="w-full pl-6 pr-2.5 py-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-emerald-600"
              />
            </div>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} data-testid="qtm-filter-cat" className="text-xs px-2 py-1.5 border border-slate-300 rounded bg-white">
              <option value="">All categories</option>
              {enums.quote_categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterRoof} onChange={e => setFilterRoof(e.target.value)} data-testid="qtm-filter-roof" className="text-xs px-2 py-1.5 border border-slate-300 rounded bg-white">
              <option value="">All roof types</option>
              {enums.roof_types.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="text-[11px] text-slate-500 ml-auto">
              {visible.length} of {rows.length} shown
            </div>
          </div>
          {/* Errors */}
          {error && (
            <div className="mx-5 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError('')}><X size={11} /></button>
            </div>
          )}
          {/* Table */}
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={14} /> Loading…
            </div>
          ) : visible.length === 0 ? (
            <p className="p-8 text-center text-sm italic text-slate-400">No quote types match the filter.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">
                      <button
                        type="button" onClick={() => cycleSort('ref')}
                        data-testid="qtm-sort-ref"
                        className="inline-flex items-center gap-1 hover:text-emerald-700"
                        title="Sort by name (click again to reverse, third click resets)"
                      >
                        Ref {sortIcon('ref')}
                      </button>
                    </th>
                    <th className="text-left px-3 py-2 font-semibold">
                      <button
                        type="button" onClick={() => cycleSort('category')}
                        data-testid="qtm-sort-category"
                        className="inline-flex items-center gap-1 hover:text-emerald-700"
                        title="Sort by category (click again to reverse, third click resets)"
                      >
                        Category {sortIcon('category')}
                      </button>
                    </th>
                    <th className="text-left px-3 py-2 font-semibold">
                      <button
                        type="button" onClick={() => cycleSort('roof')}
                        data-testid="qtm-sort-roof"
                        className="inline-flex items-center gap-1 hover:text-emerald-700"
                        title="Sort by roof type (click again to reverse, third click resets)"
                      >
                        Roof {sortIcon('roof')}
                      </button>
                    </th>
                    <th className="text-left px-3 py-2 font-semibold">Includes chars</th>
                    <th className="text-left px-3 py-2 font-semibold">Notes chars</th>
                    <th className="text-right px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visible.map(r => (
                    <tr key={r.ref} data-testid={`qtm-row-${r.ref.replace(/\s+/g, '-')}`} className="hover:bg-emerald-50/40">
                      <td className="px-3 py-2 font-semibold text-slate-900">{r.ref}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {(r.quote_categories || []).length > 0 ? (r.quote_categories || []).join(', ') : (r.quote_category || <span className="italic text-slate-400">—</span>)}
                      </td>
                      <td className="px-3 py-2 text-slate-600">
                        {(r.roof_types || []).length > 0 ? (r.roof_types || []).join(', ') : (r.roof_type || <span className="italic text-slate-400">—</span>)}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-500">{(r.description || '').length}</td>
                      <td className="px-3 py-2 font-mono text-slate-500">{(r.details || '').length}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => startEdit(r)} data-testid={`qtm-edit-${r.ref.replace(/\s+/g, '-')}`} className="p-1 text-slate-500 hover:text-emerald-700" title="Edit"><Edit3 size={11} /></button>
                        <button onClick={() => startDuplicate(r)} data-testid={`qtm-dup-${r.ref.replace(/\s+/g, '-')}`} className="p-1 text-slate-500 hover:text-emerald-700" title="Duplicate"><Copy size={11} /></button>
                        <button onClick={() => setConfirmDelete(r)} data-testid={`qtm-del-${r.ref.replace(/\s+/g, '-')}`} className="p-1 text-slate-500 hover:text-red-600" title="Delete"><Trash2 size={11} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* Edit / Add modal */}
      {editing && (
        <EditModal
          initial={editing}
          enums={enums}
          onSave={doSave}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}
      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-2">Delete &ldquo;{confirmDelete.ref}&rdquo;?</h3>
            <p className="text-xs text-slate-600 mb-4">
              Appraisals that reference this quote type keep their <code>quote_type_ref</code> string,
              but the Includes / Notes text will disappear from their Quotation Letter PDFs.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900">Cancel</button>
              <button onClick={doDelete} disabled={busy} data-testid="qtm-confirm-delete" className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50">
                <Trash2 size={12} /> {busy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit / Add modal ──────────────────────────────────────────────────
function EditModal({ initial, enums, onSave, onCancel, busy }) {
  const [row, setRow] = useState(initial);
  const isNew = !!initial._isNew;
  // iter299w — Snapshot the incoming ref at mount so we can detect a
  // rename regardless of how many times the user edits the input.
  const [originalRef] = useState(initial.ref || '');
  const setF = (k, v) => setRow(r => ({ ...r, [k]: v }));
  const toggleArr = (k, val) => setRow(r => {
    const arr = Array.isArray(r[k]) ? r[k] : [];
    return { ...r, [k]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
  });
  const nameChanged = !isNew && row.ref !== originalRef;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">{isNew ? 'New Quote Type' : `Edit "${originalRef}"`}</h3>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="qtm-modal-close"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Ref (name)</label>
            <input
              type="text" value={row.ref || ''} onChange={e => setF('ref', e.target.value)}
              data-testid="qtm-modal-ref"
              placeholder="e.g. Rescrew and Repair Longrun"
              className="w-full px-2.5 py-1.5 border rounded text-xs border-slate-300 focus:outline-none focus:border-emerald-600"
            />
            {nameChanged && (
              <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="qtm-modal-rename-warning">
                <b>⚠ Rename warning:</b> existing appraisals point at &ldquo;{originalRef}&rdquo; via <code>quote_type_ref</code>. Renaming will NOT update them — their Quotation Letters will fall back to hardcoded defaults for the per-quote-type cells (Thanks, Terms, Representative). You&apos;ll need to reopen each appraisal and re-pick the new quote type manually.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Categories</label>
              <div className="flex flex-wrap gap-1">
                {enums.quote_categories.map(c => (
                  <button key={c} type="button" onClick={() => toggleArr('quote_categories', c)}
                    data-testid={`qtm-cat-${c}`}
                    className={`px-2 py-0.5 text-[10px] rounded border ${(row.quote_categories || []).includes(c) ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'}`}
                  >{c}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Roof types</label>
              <div className="flex flex-wrap gap-1">
                {enums.roof_types.map(rt => (
                  <button key={rt} type="button" onClick={() => toggleArr('roof_types', rt)}
                    data-testid={`qtm-roof-${rt}`}
                    className={`px-2 py-0.5 text-[10px] rounded border ${(row.roof_types || []).includes(rt) ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'}`}
                  >{rt}</button>
                ))}
              </div>
            </div>
          </div>
          {[
            ['description', 'Includes (bullets)', 'One bullet per line.'],
            ['details', 'Notes (bullets)', 'One note per line.'],
            ['completion_description', 'Completion Description', 'Text shown on the Completion / after-work reports.'],
            ['completion_details', 'Completion Details', 'Extra completion-report copy.'],
          ].map(([k, label, hint]) => (
            <div key={k}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</label>
              <textarea
                value={row[k] || ''} onChange={e => setF(k, e.target.value)}
                data-testid={`qtm-modal-${k}`}
                rows={4}
                className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-[12px] font-mono focus:outline-none focus:border-emerald-600"
              />
              <p className="text-[9px] text-slate-400 mt-0.5">{hint}</p>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={() => onSave({ ...row, _originalRef: originalRef })} disabled={busy || !row.ref?.trim()}
            data-testid="qtm-modal-save"
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-emerald-700 hover:bg-emerald-800 text-white rounded disabled:opacity-50"
          >
            <Save size={12} /> {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
